import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { apiService } from '@/services/api'

// ─── Helpers ───

function generateSessionId(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

function getOrCreateSessionId(): string {
  let sid = sessionStorage.getItem('page_visit_session_id')
  if (!sid) {
    sid = generateSessionId()
    sessionStorage.setItem('page_visit_session_id', sid)
  }
  return sid
}

function getPageCategory(pathname: string): string {
  if (pathname === '/' || pathname === '') return 'home'
  if (pathname.startsWith('/login')) return 'auth'
  if (pathname.startsWith('/admin')) return 'admin'
  if (pathname.startsWith('/dashboard')) return 'dashboard'
  if (pathname.startsWith('/students')) return 'students'
  if (pathname.startsWith('/teachers')) return 'teachers'
  if (pathname.startsWith('/grades')) return 'grades'
  if (pathname.startsWith('/timetable')) return 'timetable'
  if (pathname.startsWith('/discipline')) return 'discipline'
  if (pathname.startsWith('/budget')) return 'budget'
  if (pathname.startsWith('/content')) return 'content'
  if (pathname.startsWith('/audit')) return 'audit'
  if (pathname.startsWith('/settings')) return 'settings'
  if (pathname.startsWith('/fees')) return 'fees'
  if (pathname.startsWith('/academics')) return 'academics'
  if (pathname.startsWith('/statistics')) return 'statistics'
  if (pathname.startsWith('/messages')) return 'messages'
  if (pathname.startsWith('/news')) return 'news'
  if (pathname.startsWith('/events')) return 'events'
  if (pathname.startsWith('/forum')) return 'forum'
  if (pathname.startsWith('/about')) return 'about'
  if (pathname.startsWith('/history')) return 'history'
  return 'other'
}

interface PendingVisit {
  page_path: string
  page_category: string
  session_id: string
  duration_ms: number
  timestamp: string
}

// ─── Hook ───

export function usePageVisitTracker() {
  const location = useLocation()
  const sessionIdRef = useRef<string>(getOrCreateSessionId())
  const currentPageRef = useRef<{ path: string; startTime: number } | null>(null)
  const pendingRef = useRef<PendingVisit | null>(null)

  // Envoie une visite au backend (silencieux — pas de toast en cas d'erreur)
  const sendVisit = async (visit: PendingVisit) => {
    try {
      await apiService.recordPageVisit(visit)
    } catch {
      // Silencieux : le tracking ne doit jamais bloquer l'UX
    }
  }

  // Envoie la visite précédente quand on change de page
  useEffect(() => {
    const now = Date.now()
    const newPath = location.pathname + location.search

    // S'il y a une page en cours, on la finalise et on l'envoie
    if (currentPageRef.current) {
      const duration = now - currentPageRef.current.startTime
      const visit: PendingVisit = {
        page_path: currentPageRef.current.path,
        page_category: getPageCategory(currentPageRef.current.path),
        session_id: sessionIdRef.current,
        duration_ms: duration,
        timestamp: new Date().toISOString(),
      }
      pendingRef.current = visit
      sendVisit(visit)
    }

    // Démarre le suivi de la nouvelle page
    currentPageRef.current = {
      path: newPath,
      startTime: now,
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search])

  // Envoie la dernière page avant fermeture de l'onglet
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (currentPageRef.current) {
        const duration = Date.now() - currentPageRef.current.startTime
        const visit: PendingVisit = {
          page_path: currentPageRef.current.path,
          page_category: getPageCategory(currentPageRef.current.path),
          session_id: sessionIdRef.current,
          duration_ms: duration,
          timestamp: new Date().toISOString(),
        }
        // Utilise keepalive pour garantir l'envoi même si l'onglet se ferme
        const url = `${(import.meta as any).env?.VITE_API_URL || 'http://127.0.0.1:8001/api/v1'}/audit/page-visits`
        const token = localStorage.getItem('auth_token') ||
          (() => {
            const stored = localStorage.getItem('auth-storage')
            if (stored) {
              try {
                const parsed = JSON.parse(stored)
                return parsed.state?.accessToken || null
              } catch { return null }
            }
            return null
          })()

        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        }
        if (token) headers['Authorization'] = `Bearer ${token}`

        navigator.sendBeacon?.(
          url,
          new Blob([JSON.stringify(visit)], { type: 'application/json' })
        ) ?? fetch(url, {
          method: 'POST',
          headers,
          body: JSON.stringify(visit),
          keepalive: true,
        })
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [])

  // Visibilité de la page : pause/reprise du chronomètre
  useEffect(() => {
    let hiddenTime: number | null = null

    const handleVisibilityChange = () => {
      if (document.hidden) {
        hiddenTime = Date.now()
      } else if (hiddenTime !== null && currentPageRef.current) {
        // On ajuste le startTime pour ne pas compter le temps caché
        const hiddenDuration = Date.now() - hiddenTime
        currentPageRef.current.startTime += hiddenDuration
        hiddenTime = null
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [])
}